/**
 * Message reactions for the web PM client.
 *
 * Window-attached (this file is concatenated into the `interpals` bundle, it is
 * not an ES module). Renders/updates reaction pills, opens the quick-bar + full
 * emoji picker, talks to the REST API (POST/DELETE /v1/message-reaction via
 * ApiClient), and applies live websocket updates (the `thread:reaction` custom
 * event from websocket-client). The authoritative aggregate after a change is
 * delivered by that socket event, which also refreshes the inbox list.
 */
(function () {
  'use strict';

  var POPOVER_ID = 'pm-reaction-popover';

  function currentUserId() {
    var meta = document.querySelector('meta[name="user-id"]');
    return meta ? String(meta.getAttribute('content') || '') : '';
  }

  // Display name of the open thread's contact, used to attribute reactions in
  // the chip tooltip ("You reacted 😂" vs "{name} reacted 😂"). In a 1-on-1
  // thread a non-viewer reaction can only be the contact.
  function contactName() {
    var el = document.getElementById('current_thread_username');
    return el ? (el.textContent || '').trim() : '';
  }

  // --- Reaction attribution tooltips --------------------------------------

  function reactionChipTitle(chip) {
    if (!window.t) {
      return null;
    }
    var emoji = chip.dataset.emoji || '';
    if (chip.dataset.reacted === '1') {
      return window.t('legacy.pm.reaction_preview_you', { emoji: emoji });
    }
    var name = contactName();
    if (!name) {
      return null;
    }
    return window.t('legacy.pm.reaction_preview_other', { name: name, emoji: emoji });
  }

  // Attribute every reaction chip in `root` (defaults to the whole document) so
  // hovering a chip reveals who reacted. Covers server-rendered chips and the
  // chips renderPills rebuilds.
  function applyReactionTooltips(root) {
    (root || document).querySelectorAll('.pm-reaction-chip').forEach(function (chip) {
      var title = reactionChipTitle(chip);
      if (title) {
        chip.setAttribute('title', title);
      }
    });
  }

  // --- Adaptive toolbar placement ----------------------------------------

  // Gutter width (px) the hover toolbar needs beside the bubble: the ~116px pill
  // plus its margin and a small safety gap.
  var TOOLBAR_GUTTER_NEEDED = 128;

  // The hover toolbar sits in the gutter beside the bubble when there is room.
  // Wide bubbles leave too little gutter, so the pill would run off the
  // conversation edge (and be clipped by the row's overflow-x). For those, mark
  // the wrapper so CSS overlays the pill on the bubble's top trailing corner
  // instead. The gutter is measured against each message's own row, which is the
  // element that bounds (and clips) the toolbar, so this works regardless of how
  // the surrounding layout containers are named or sized. Reads are batched
  // before writes to avoid layout thrash on long threads.
  function positionMessageToolbars() {
    var wraps = document.querySelectorAll('.pm-msg-bubble-wrap');
    var decisions = [];
    wraps.forEach(function (wrap) {
      var bubble = wrap.querySelector(':scope > .msg_body');
      var row = wrap.closest('.pm_msg');
      if (!bubble || !row) {
        return;
      }
      var rowRect = row.getBoundingClientRect();
      var wrapRect = wrap.getBoundingClientRect();
      var isOwn = bubble.className.indexOf('msg_body--own') > -1;
      var gutter = isOwn ? (wrapRect.left - rowRect.left) : (rowRect.right - wrapRect.right);
      decisions.push({ wrap: wrap, overlay: gutter < TOOLBAR_GUTTER_NEEDED });
    });
    decisions.forEach(function (d) {
      d.wrap.classList.toggle('msg-actions-overlay', d.overlay);
    });
    // A class change can move the hovered toolbar; keep it within view.
    clampHoveredToolbar();
  }

  // --- Keep the hovered toolbar within the visible scroll area ------------

  // A message can be taller than the viewport. Its toolbar (overlay: anchored to
  // the bubble bottom; gutter: vertically centred) can then scroll out of reach.
  // While hovering such a message, pin the toolbar into the visible intersection
  // of the bubble and the scroll viewport (below the conversation header): the
  // overlay toolbar stays near the bottom of that area, the gutter toolbar near
  // the top. When the toolbar's default position is already fully visible, leave
  // CSS in charge so short messages keep the nicer centred/overlay placement.
  var msgScroller = null;
  var activeHoverWrap = null;

  function getMsgScroller() {
    if (msgScroller && document.contains(msgScroller)) {
      return msgScroller;
    }
    var node = document.querySelector('.pm_msg');
    while (node) {
      var style = window.getComputedStyle(node);
      if ((style.overflowY === 'auto' || style.overflowY === 'scroll') &&
          node.scrollHeight > node.clientHeight + 4) {
        msgScroller = node;
        return node;
      }
      node = node.parentElement;
    }
    msgScroller = document.getElementById('threads_right');
    return msgScroller;
  }

  // A toolbar holding an open actions dropdown keeps its clamped offsets. The
  // dropdown is anchored to the toggle button's viewport rect, so snapping the
  // pill back to its CSS placement would leave the menu hanging off nothing.
  // closeMessageActionMenus() clears the offsets when it un-pins the toolbar.
  function clearToolbarPin(toolbar) {
    if (toolbar && !toolbar.classList.contains('msg-actions-open')) {
      toolbar.style.top = '';
      toolbar.style.bottom = '';
    }
  }

  function clampHoveredToolbar() {
    if (!activeHoverWrap) {
      return;
    }
    var toolbar = activeHoverWrap.querySelector(':scope > .pm-msg-toolbar');
    var scroller = getMsgScroller();
    if (!toolbar || !scroller) {
      return;
    }
    var scrollerRect = scroller.getBoundingClientRect();
    var wrapRect = activeHoverWrap.getBoundingClientRect();
    // Measure the inner pill, not the toolbar element: in gutter mode the toolbar
    // is full bubble height (it centres the pill via flex), so its own height is
    // not the pill's.
    var pill = toolbar.firstElementChild;
    var height = (pill ? pill.offsetHeight : 0) || 40;
    var isOverlay = activeHoverWrap.classList.contains('msg-actions-overlay');

    // Top of the usable area: below the conversation header if it overlaps.
    var topBound = scrollerRect.top;
    var header = document.getElementById('conversation-header');
    if (header && !header.classList.contains('hidden')) {
      topBound = Math.max(topBound, header.getBoundingClientRect().bottom);
    }
    topBound += 8;
    var bottomBound = scrollerRect.bottom - 8;

    // The toolbar's natural viewport top with CSS alone. Overlay default is
    // bottom: -1rem, so the pill overhangs 1rem below the bubble's bottom edge.
    var naturalTop = isOverlay
      ? (wrapRect.bottom + 16 - height)
      : (wrapRect.top + (wrapRect.height - height) / 2);

    // Fully reachable already: let CSS handle it (centred gutter / bottom overlay).
    if (naturalTop >= topBound && naturalTop + height <= bottomBound) {
      clearToolbarPin(toolbar);
      return;
    }

    // Otherwise pin into the bubble's visible area. Overlay tracks the bottom of
    // the visible region (matching its default bottom placement); gutter tracks
    // the top.
    var top;
    if (isOverlay) {
      top = Math.max(Math.min(naturalTop, bottomBound - height), topBound);
    } else {
      top = Math.max(wrapRect.top + 8, topBound);
      top = Math.min(top, Math.min(wrapRect.bottom, bottomBound) - height);
    }
    toolbar.style.top = (top - wrapRect.top) + 'px';
    toolbar.style.bottom = 'auto';
  }

  // closeMessageActionMenus() drops the offsets held for an open dropdown and
  // needs them re-derived for whichever message is still hovered.
  window.clampHoveredMessageToolbar = clampHoveredToolbar;

  function parseEmojiAttr(name) {
    var root = document.getElementById('threads_main');
    if (!root) {
      return [];
    }
    try {
      var parsed = JSON.parse(root.getAttribute(name) || '[]');
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }

  function quickReactions() {
    return parseEmojiAttr('data-pm-quick-reactions');
  }

  // Full swipeable-strip set (the quick set first, then extras that scroll into
  // view under the floating "+"). Falls back to the quick set if the strip
  // attribute is absent (older rendered page).
  function reactionStrip() {
    var strip = parseEmojiAttr('data-pm-reaction-strip');
    return strip.length ? strip : quickReactions();
  }

  // Translate via the JS i18n bridge, falling back to English if the bridge is
  // absent or the key is missing (some libs echo the key back on a miss).
  function tr(key, fallback) {
    if (typeof window.t !== 'function') {
      return fallback;
    }
    var value = window.t(key);
    return !value || value === key ? fallback : value;
  }

  // Tailwind `sm` breakpoint. Below it the full picker opens as a bottom sheet
  // (mirroring the native app); at or above it the in-place picker popover.
  function isNarrowViewport() {
    return typeof window.matchMedia === 'function' && window.matchMedia('(max-width: 639px)').matches;
  }

  function attrEscape(value) {
    return String(value).replace(/["\\]/g, '\\$&');
  }

  function reactionsContainer(messageId) {
    return document.querySelector('.pm-reactions[data-reactions-for="' + attrEscape(messageId) + '"]');
  }

  // Refreshing the left thread list re-renders the inbox snippet (the
  // "You reacted 😂" / "{name} reacted 😂" preview), mirroring how a new
  // message updates it. Debounced so the local request + the websocket echo
  // of the same reaction collapse into a single refresh.
  var threadListRefreshTimer = null;
  function refreshThreadList() {
    if (threadListRefreshTimer) {
      return;
    }
    threadListRefreshTimer = setTimeout(function () {
      threadListRefreshTimer = null;
      var fn = window.pm_view_mode === 'paged' ? window.update_pm_info_paged : window.update_pm_info;
      if (typeof fn === 'function') {
        // Force a thread-list rebuild: a reaction changes the inbox snippet
        // without bumping max_msg_id, so the default poll would skip it.
        fn(true);
      }
    }, 250);
  }

  // --- Optimistic state helpers -------------------------------------------

  // Read the pills currently in the DOM as plain { emoji, count } data so we can
  // apply the viewer's change locally before the server confirms.
  function readPills(container) {
    var pills = [];
    if (!container) {
      return pills;
    }
    container.querySelectorAll('.pm-reaction-chip').forEach(function (chip) {
      var countEl = chip.querySelector('.pm-reaction-count');
      pills.push({
        emoji: chip.dataset.emoji,
        count: parseInt(countEl ? countEl.textContent : '0', 10) || 0
      });
    });
    return pills;
  }

  function incrementPill(pills, emoji) {
    var found = false;
    var out = pills.map(function (p) {
      if (p.emoji === emoji) {
        found = true;
        return { emoji: p.emoji, count: p.count + 1 };
      }
      return p;
    });
    if (!found) {
      out.push({ emoji: emoji, count: 1 });
    }
    return out;
  }

  function decrementPill(pills, emoji) {
    return pills
      .map(function (p) {
        return p.emoji === emoji ? { emoji: p.emoji, count: p.count - 1 } : p;
      })
      .filter(function (p) {
        return p.count > 0;
      });
  }

  function snapshotReactions(messageId) {
    var container = reactionsContainer(messageId);
    return { pills: readPills(container), mine: container ? ownReaction(container) : null };
  }

  function revertReactions(messageId, snapshot) {
    if (snapshot) {
      renderPills(messageId, snapshot.pills, snapshot.mine);
    }
  }

  // --- Server calls -------------------------------------------------------

  // Per-message operation counter. Rapidly toggling a reaction fires overlapping
  // requests. Only the response/revert of the most recent operation for a given
  // message may touch the DOM, so a slow or failed earlier request cannot clobber
  // the newer state (its own or the websocket echo's).
  var reactionOpSeq = {};

  function beginReactionOp(messageId) {
    var key = String(messageId);
    reactionOpSeq[key] = (reactionOpSeq[key] || 0) + 1;
    return reactionOpSeq[key];
  }

  function isLatestReactionOp(messageId, op) {
    return reactionOpSeq[String(messageId)] === op;
  }

  // Reconcile after a reaction request settles. The optimistic pills already
  // reflect the change; the authoritative aggregate arrives on the
  // thread:reaction socket event (which also refreshes the inbox). A failed
  // request reverts to the snapshot. Stale ops (a newer click superseded this
  // one) are ignored via the per-message op token.
  function settleReaction(requestPromise, messageId, snapshot, op) {
    window.pm_ajax_request_active = 1;
    return requestPromise
      .then(function (res) {
        if (!isLatestReactionOp(messageId, op)) {
          return null;
        }
        if (!res || !res.ok) {
          revertReactions(messageId, snapshot);
          return null;
        }
        refreshThreadList();
        // Both POST and DELETE return the message's authoritative aggregate, so
        // pills reconcile from the response even if the thread:reaction socket
        // echo is missed (e.g. the tab's websocket is disconnected). This settles
        // the optimistic add/remove to server-authoritative state.
        var type = res.headers && res.headers.get ? res.headers.get('content-type') : '';
        if (!type || type.indexOf('application/json') === -1) {
          return null;
        }
        return res.json().then(function (data) {
          if (isLatestReactionOp(messageId, op) && data && Array.isArray(data.reactions)) {
            renderPills(messageId, data.reactions, data.my_reaction || null);
          }
        }).catch(function () {});
      })
      .catch(function () {
        if (isLatestReactionOp(messageId, op)) {
          revertReactions(messageId, snapshot);
        }
      })
      .then(function () {
        window.pm_ajax_request_active = 0;
      });
  }

  window.applyReaction = function (messageId, emoji) {
    var tid = window.get_active_thread_id ? window.get_active_thread_id() : null;
    if (!tid || !window.ApiClient) {
      return;
    }
    closePopover();

    // Render the new pill state immediately; the socket event reconciles the
    // true counts and a failed request reverts to the snapshot.
    var snapshot = snapshotReactions(messageId);
    var op = beginReactionOp(messageId);
    var prev = snapshot.mine;
    if (prev !== emoji) {
      var pills = readPills(reactionsContainer(messageId));
      if (prev) {
        pills = decrementPill(pills, prev);
      }
      renderPills(messageId, incrementPill(pills, emoji), emoji);
    }

    var body = new URLSearchParams({
      thread_id: String(tid),
      message_id: String(messageId),
      reaction: emoji
    }).toString();
    settleReaction(window.ApiClient.request('/message-reaction', {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: body
    }), messageId, snapshot, op);
  };

  window.removeReaction = function (messageId) {
    var tid = window.get_active_thread_id ? window.get_active_thread_id() : null;
    if (!tid || !window.ApiClient) {
      return;
    }

    var snapshot = snapshotReactions(messageId);
    var op = beginReactionOp(messageId);
    if (snapshot.mine) {
      renderPills(messageId, decrementPill(snapshot.pills, snapshot.mine), null);
    }

    var query = new URLSearchParams({
      thread_id: String(tid),
      message_id: String(messageId)
    }).toString();
    settleReaction(window.ApiClient.request('/message-reaction?' + query, {
      method: 'DELETE'
    }), messageId, snapshot, op);
  };

  // Clicking an existing chip removes the reaction if it is the viewer's own,
  // otherwise switches the viewer's reaction to that emoji.
  window.toggleReaction = function (event, messageId, emoji) {
    if (event) {
      event.stopPropagation();
    }
    var chip = event && event.currentTarget;
    if (chip && chip.dataset && chip.dataset.reacted === '1') {
      window.removeReaction(messageId);
    } else {
      window.applyReaction(messageId, emoji);
    }
  };

  // --- Quick-bar + full picker popover ------------------------------------

  function onDocClick(e) {
    var pop = document.getElementById(POPOVER_ID);
    if (pop && !pop.contains(e.target)) {
      closePopover();
    }
  }

  // The popover is positioned once against the trigger's viewport rect, so any
  // scroll (including the inner message-list container, hence capture) or resize
  // would detach it from the trigger. Dismiss instead of trying to re-anchor.
  // Swiping the emoji strip itself also fires a (captured) scroll event, so
  // ignore scrolls that originate inside the popover.
  function onViewportChange(e) {
    var pop = document.getElementById(POPOVER_ID);
    // resize fires with e.target === window (not a Node), and scroll can fire
    // with a non-element target, so gate the containment check on an actual
    // Node. A non-Node target (window resize) falls through to closePopover(),
    // which is the intended behavior anyway.
    if (pop && e && e.target instanceof Node && pop.contains(e.target)) {
      return;
    }
    closePopover();
  }

  function closePopover() {
    var pop = document.getElementById(POPOVER_ID);
    if (pop) {
      pop.remove();
    }
    document.removeEventListener('click', onDocClick, true);
    window.removeEventListener('scroll', onViewportChange, true);
    window.removeEventListener('resize', onViewportChange);
  }

  window.openReactionPicker = function (event, messageId) {
    if (event) {
      event.stopPropagation();
    }
    closePopover();
    // The trigger sits on the pinned toolbar, which the outside-click handler
    // exempts, so an actions dropdown open on this message would stay open behind
    // the picker.
    window.closeMessageActionMenus();

    var trigger = event && event.currentTarget;
    var pop = document.createElement('div');
    pop.id = POPOVER_ID;
    pop.className = 'pm-reaction-popover absolute z-50 flex rounded-full ' +
      'bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 shadow-lg ' +
      'max-w-[min(20rem,92vw)]';

    // Horizontally scrollable emoji row. Trailing padding leaves room for the
    // floating "+" so the last emoji can scroll out from under it.
    var scroll = document.createElement('div');
    scroll.className = 'pm-reaction-scroll scrollbar-hide flex items-center gap-1 ' +
      'overflow-x-auto py-1.5 pl-2 pr-[5rem] rounded-full min-w-0';

    reactionStrip().forEach(function (emoji) {
      var b = document.createElement('button');
      b.type = 'button';
      b.className = 'pm-quick-emoji flex-none w-11 h-11 flex items-center justify-center text-xl rounded-full ' +
        'hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors';
      b.textContent = emoji;
      b.addEventListener('click', function (e) {
        e.stopPropagation();
        window.applyReaction(messageId, emoji);
      });
      scroll.appendChild(b);
    });
    pop.appendChild(scroll);

    // Floating "+" pinned at the trailing edge. Its gradient background fades
    // the emoji out as they scroll under it, then sits on a solid panel so the
    // button stays legible over anything behind it. The wrap is click-through
    // (pointer-events-none) so a swipe starting over its fade area still scrolls
    // the strip; only the button itself captures taps.
    var moreWrap = document.createElement('div');
    moreWrap.className = 'pm-reaction-more-wrap absolute right-0 top-0 bottom-0 flex items-center ' +
      'pl-6 pr-1.5 rounded-r-full bg-gradient-to-r from-white/0 via-white to-white ' +
      'dark:from-gray-800/0 dark:via-gray-800 dark:to-gray-800 pointer-events-none';

    var more = document.createElement('button');
    more.type = 'button';
    more.className = 'pm-react-more flex-none w-11 h-11 flex items-center justify-center rounded-full ' +
      'text-gray-500 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-700 transition-colors pointer-events-auto';
    more.innerHTML = '<i data-lucide="plus"></i>';
    more.setAttribute('aria-label', tr('legacy.pm.add_reaction', 'React'));
    more.addEventListener('click', function (e) {
      e.stopPropagation();
      if (isNarrowViewport()) {
        openFullPickerSheet(messageId, trigger);
      } else {
        openFullPicker(pop, messageId, trigger);
      }
    });
    moreWrap.appendChild(more);
    pop.appendChild(moreWrap);

    document.body.appendChild(pop);
    positionPopover(pop, trigger);

    if (window.LucideIcons) {
      window.LucideIcons.initialize(pop);
    }

    // Defer so this same click does not immediately close the popover.
    setTimeout(function () {
      document.addEventListener('click', onDocClick, true);
      window.addEventListener('scroll', onViewportChange, true);
      window.addEventListener('resize', onViewportChange);
    }, 0);
  };

  // Clamp the popover inside the viewport on both axes. Preferred anchor is just
  // above the trigger; if it doesn't fit there it flips below, and either way the
  // top/left are pinned so a tall full picker can't run off the screen edges.
  function positionPopover(pop, trigger) {
    if (!trigger) {
      return;
    }
    var rect = trigger.getBoundingClientRect();
    var viewportW = document.documentElement.clientWidth;
    var viewportH = document.documentElement.clientHeight;
    var popW = pop.offsetWidth;
    var popH = pop.offsetHeight;

    var top = rect.top - popH - 8;
    if (top < 8) {
      var below = rect.bottom + 8;
      top = below + popH <= viewportH - 8 ? below : Math.max(8, viewportH - popH - 8);
    }

    var left = rect.left;
    var maxLeft = viewportW - popW - 8;
    if (left > maxLeft) {
      left = maxLeft;
    }
    if (left < 8) {
      left = 8;
    }

    pop.style.top = window.scrollY + top + 'px';
    pop.style.left = window.scrollX + left + 'px';
  }

  function openFullPicker(pop, messageId, trigger) {
    // Lazy-load the framework-agnostic emoji picker web component so it stays
    // off the critical path (only needed on the "+" click).
    import('emoji-picker-element').then(function () {
      // The popover may have been dismissed (scroll/resize/click-outside) before
      // the picker bundle resolved. Building into the detached node would create
      // an invisible picker, so bail.
      if (!document.body.contains(pop)) {
        return;
      }
      pop.innerHTML = '';
      pop.classList.remove('rounded-full', 'flex', 'max-w-[min(20rem,92vw)]');
      pop.classList.add('rounded-xl', 'overflow-hidden');
      var picker = document.createElement('emoji-picker');
      // Match the site theme. The component defaults to prefers-color-scheme,
      // which renders dark even when the site is in light mode.
      picker.className = 'pm-full-picker ' +
        (document.documentElement.classList.contains('dark') ? 'dark' : 'light');
      picker.addEventListener('emoji-click', function (ev) {
        var emoji = ev.detail && ev.detail.unicode;
        if (emoji) {
          window.applyReaction(messageId, emoji);
        }
      });
      pop.appendChild(picker);
      // The full picker is far larger than the quick-bar, so re-anchor it within
      // the viewport once it has laid out (the web component sizes async). Move
      // focus into the picker so the "+" button (now removed) does not strand
      // keyboard focus.
      requestAnimationFrame(function () {
        positionPopover(pop, trigger);
        picker.focus();
      });
    }).catch(function () {
      // Picker bundle failed to load. Leave the quick-bar in place.
    });
  }

  var SHEET_ID = 'pm-reaction-sheet';
  var SHEET_ANIM_MS = 200;

  // Mobile-web full picker: a slide-up bottom sheet (mirrors the native app),
  // used below the `sm` breakpoint where the in-place popover is too cramped.
  // `trigger` is the toolbar button that opened the strip, so focus can return
  // to it on close.
  function openFullPickerSheet(messageId, trigger) {
    closePopover();
    if (document.getElementById(SHEET_ID)) {
      return;
    }

    var isDark = document.documentElement.classList.contains('dark');
    // Restore focus here on close. The trigger outlives the sheet (the toolbar
    // button); the "+" that was clicked is already gone with the popover.
    var previousFocus = (trigger && typeof trigger.focus === 'function')
      ? trigger
      : document.activeElement;
    var reducedMotion = typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    var backdrop = document.createElement('div');
    backdrop.id = SHEET_ID;
    backdrop.className = 'fixed inset-0 z-[10000] bg-black/50 flex flex-col justify-end ' +
      'opacity-0 transition-opacity duration-200 motion-reduce:transition-none';
    backdrop.setAttribute('role', 'dialog');
    backdrop.setAttribute('aria-modal', 'true');
    backdrop.setAttribute('aria-labelledby', SHEET_ID + '-title');

    var panel = document.createElement('div');
    panel.className = 'pm-reaction-sheet-panel bg-white dark:bg-gray-900 rounded-t-2xl shadow-2xl ' +
      'dark:shadow-none dark:ring-1 dark:ring-white/10 ' +
      'w-full max-h-[80dvh] flex flex-col overflow-hidden translate-y-full ' +
      'transition-transform duration-200 ease-out motion-reduce:transition-none';

    var header = document.createElement('div');
    header.className = 'flex-shrink-0 relative flex items-center justify-center px-4 pt-4 pb-2 ' +
      'border-b border-gray-100 dark:border-gray-800';

    var handle = document.createElement('div');
    handle.className = 'absolute top-1.5 left-1/2 -translate-x-1/2 w-9 h-1 rounded-full ' +
      'bg-gray-300 dark:bg-gray-600';

    var title = document.createElement('h2');
    title.id = SHEET_ID + '-title';
    title.className = 'text-base font-semibold text-gray-900 dark:text-gray-100';
    title.textContent = tr('legacy.pm.add_reaction', 'React');

    var closeBtn = document.createElement('button');
    closeBtn.type = 'button';
    closeBtn.className = 'absolute right-2 top-1/2 -translate-y-1/2 p-3 rounded-full ' +
      'hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors';
    closeBtn.setAttribute('aria-label', tr('common.close', 'Close'));
    closeBtn.innerHTML = '<i data-lucide="x" class="w-5 h-5 text-gray-500 dark:text-gray-400"></i>';

    header.appendChild(handle);
    header.appendChild(title);
    header.appendChild(closeBtn);

    var body = document.createElement('div');
    body.className = 'flex-1 min-h-0 overflow-hidden pb-[env(safe-area-inset-bottom,0px)]';

    panel.appendChild(header);
    panel.appendChild(body);
    backdrop.appendChild(panel);
    document.body.appendChild(backdrop);

    window.scrollLock?.lock();
    if (window.LucideIcons) {
      window.LucideIcons.initialize(backdrop);
    }

    // Set synchronously on the first close so overlapping triggers (backdrop tap
    // then Escape within the 200ms close animation, while the node is still
    // connected) cannot schedule a second scrollLock.unlock() and undercount a
    // reference-counted lock shared with another open sheet.
    var sheetClosing = false;

    function closeSheet() {
      if (sheetClosing) {
        return;
      }
      sheetClosing = true;
      backdrop.classList.add('opacity-0');
      panel.classList.add('translate-y-full');
      document.removeEventListener('keydown', onKey, true);
      setTimeout(function () {
        backdrop.remove();
        window.scrollLock?.unlock();
        // Return focus to the trigger so keyboard/screen-reader users are not
        // stranded on the removed sheet.
        if (previousFocus && previousFocus.isConnected && typeof previousFocus.focus === 'function') {
          previousFocus.focus();
        }
      }, reducedMotion ? 0 : SHEET_ANIM_MS);
    }

    function onKey(e) {
      if (e.key === 'Escape') {
        e.stopPropagation();
        closeSheet();
        return;
      }
      // Keep focus inside the dialog: wrap Shift+Tab off the close button back to
      // the picker. The emoji-picker element manages its own internal Tab order.
      if (e.key === 'Tab' && e.shiftKey && document.activeElement === closeBtn) {
        var picker = body.querySelector('emoji-picker');
        if (picker) {
          e.preventDefault();
          picker.focus();
        }
      }
    }

    backdrop.addEventListener('click', function (e) {
      if (e.target === backdrop) {
        closeSheet();
      }
    });
    closeBtn.addEventListener('click', function (e) {
      e.stopPropagation();
      closeSheet();
    });
    document.addEventListener('keydown', onKey, true);

    function revealSheet() {
      backdrop.classList.remove('opacity-0');
      panel.classList.remove('translate-y-full');
      // Move focus into the dialog (the close button is reachable immediately;
      // focus shifts to the picker once it mounts) so `aria-modal` holds.
      closeBtn.focus();
    }

    if (reducedMotion) {
      revealSheet();
    } else {
      // Double rAF so the from-state (translate-y-full / opacity-0) paints before
      // the transition to the settled state. Bail if a fast Escape/backdrop tap
      // closed the sheet within this frame, or rAF2 would undo the close.
      requestAnimationFrame(function () {
        requestAnimationFrame(function () {
          if (sheetClosing) {
            return;
          }
          revealSheet();
        });
      });
    }

    import('emoji-picker-element').then(function () {
      // The sheet may have been dismissed before the picker bundle resolved.
      if (sheetClosing) {
        return;
      }
      var picker = document.createElement('emoji-picker');
      picker.className = 'pm-full-picker w-full h-full ' + (isDark ? 'dark' : 'light');
      picker.addEventListener('emoji-click', function (ev) {
        var emoji = ev.detail && ev.detail.unicode;
        if (emoji) {
          window.applyReaction(messageId, emoji);
          closeSheet();
        }
      });
      body.appendChild(picker);
      picker.focus();
    }).catch(function () {
      closeSheet();
    });
  }

  // --- Pill rendering -----------------------------------------------------

  function renderPills(messageId, reactions, myReaction) {
    var container = reactionsContainer(messageId);
    if (!container) {
      return;
    }
    container.innerHTML = '';
    if (!reactions || !reactions.length) {
      container.classList.add('hidden');
      return;
    }
    container.classList.remove('hidden');

    reactions.forEach(function (r) {
      var mine = myReaction && String(myReaction) === String(r.emoji);
      var chip = document.createElement('button');
      chip.type = 'button';
      chip.className = 'pm-reaction-chip inline-flex items-center gap-1 px-2 h-7 rounded-full text-sm border transition-colors ' +
        (mine
          ? 'bg-fuchsia-50 border-fuchsia-200 text-fuchsia-700 dark:bg-fuchsia-900 dark:border-fuchsia-700 dark:text-fuchsia-100'
          : 'bg-gray-100 border-gray-200 text-gray-700 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200');
      chip.dataset.emoji = r.emoji;
      if (mine) {
        chip.dataset.reacted = '1';
      }
      chip.addEventListener('click', function (e) {
        window.toggleReaction(e, messageId, r.emoji);
      });

      var emojiSpan = document.createElement('span');
      emojiSpan.className = 'pm-reaction-emoji leading-none';
      emojiSpan.textContent = r.emoji;
      var countSpan = document.createElement('span');
      countSpan.className = 'pm-reaction-count text-xs font-medium';
      countSpan.textContent = r.count;

      chip.appendChild(emojiSpan);
      chip.appendChild(countSpan);
      container.appendChild(chip);
    });

    applyReactionTooltips(container);
  }

  function ownReaction(container) {
    var mine = container.querySelector('.pm-reaction-chip[data-reacted="1"]');
    return mine ? mine.dataset.emoji : null;
  }

  // --- Live updates from the websocket ------------------------------------

  // Gated to the PM page: this module ships in the global interpals bundle,
  // but the thread list and message containers it refreshes exist only on
  // /app/messages. An ungated listener would force a thread-list rebuild
  // poll from whatever page the user is on when a reaction arrives.
  if (document.querySelector('.privateMessages')) {
    document.addEventListener('thread:reaction', function (e) {
      var detail = e.detail || {};

      // Update the inbox snippet for any thread (not just the open one), the same
      // way a new message refreshes the left list.
      refreshThreadList();

      var activeThreadId = window.get_active_thread_id ? window.get_active_thread_id() : null;
      if (activeThreadId && String(activeThreadId) !== String(detail.thread_id)) {
        return;
      }

      var container = reactionsContainer(detail.message_id);
      if (!container) {
        return;
      }

      // The viewer's own reaction only changes when this event is from one of
      // their own devices. Otherwise keep whatever the pills already reflect.
      var myReaction = ownReaction(container);
      if (String(detail.reactor_uid) === currentUserId()) {
        myReaction = detail.action === 'set' ? detail.reaction : null;
      }

      renderPills(detail.message_id, detail.reactions || [], myReaction);
    });
  }

  // Server-rendered chips (initial load, thread switches, loaded older pages)
  // arrive without tooltips. Attribute them on load and whenever the message
  // list mutates.
  function initReactionTooltips() {
    applyReactionTooltips(document);
    positionMessageToolbars();
    if (!window.MutationObserver) {
      return;
    }

    // The thread message list lives under different container ids depending on
    // the PM layout, so resolve whichever exists.
    var msgContainer = document.getElementById('conversation')
      || document.getElementById('messages_wrapper')
      || document.getElementById('threads_right');
    if (msgContainer) {
      var scheduled = false;
      new MutationObserver(function () {
        if (scheduled) {
          return;
        }
        scheduled = true;
        // Coalesce bursts of inserted messages into a single pass.
        requestAnimationFrame(function () {
          scheduled = false;
          applyReactionTooltips(msgContainer);
          positionMessageToolbars();
        });
      }).observe(msgContainer, { childList: true, subtree: true });
    }

    // The contact name ("{name} reacted X") is needed to attribute the other
    // party's reactions. On a direct thread load the header name is filled in
    // after this first pass, so re-attribute whenever it changes (also covers
    // thread switches).
    var nameEl = document.getElementById('current_thread_username');
    if (nameEl) {
      new MutationObserver(function () {
        applyReactionTooltips(document);
      }).observe(nameEl, { childList: true, characterData: true, subtree: true });
    }

    // Available gutter changes with the viewport, so re-evaluate overlay vs
    // gutter placement on resize (debounced).
    var resizeTimer = null;
    window.addEventListener('resize', function () {
      if (resizeTimer) {
        clearTimeout(resizeTimer);
      }
      resizeTimer = setTimeout(positionMessageToolbars, 150);
    });

    // Track the hovered message so its toolbar can be kept within the visible
    // area as the user scrolls a message taller than the viewport.
    document.addEventListener('mouseover', function (e) {
      var wrap = e.target.closest && e.target.closest('.pm-msg-bubble-wrap');
      if (wrap && wrap !== activeHoverWrap) {
        activeHoverWrap = wrap;
      }
      if (wrap) {
        clampHoveredToolbar();
      }
    });
    document.addEventListener('mouseout', function (e) {
      if (!activeHoverWrap) {
        return;
      }
      var wrap = e.target.closest && e.target.closest('.pm-msg-bubble-wrap');
      if (wrap === activeHoverWrap && !wrap.contains(e.relatedTarget)) {
        clearToolbarPin(wrap.querySelector(':scope > .pm-msg-toolbar'));
        activeHoverWrap = null;
      }
    });
    var scroller = getMsgScroller();
    if (scroller) {
      var scrollScheduled = false;
      scroller.addEventListener('scroll', function () {
        if (scrollScheduled) {
          return;
        }
        scrollScheduled = true;
        requestAnimationFrame(function () {
          scrollScheduled = false;
          clampHoveredToolbar();
        });
      }, { passive: true });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initReactionTooltips);
  } else {
    initReactionTooltips();
  }
})();
